import { useState, memo } from 'react';
import { Button, Select, TextInput, ActionIcon } from '@mantine/core';
import TokenSelector from './TokenSelector';

export const OverrideRuleEditor = memo(({ initialConditions, initialOverrideFields, onSave, allColumns, availableTokenFields = [] }) => {
  const [conditions, setConditions] = useState(initialConditions);
  const [overrideFields, setOverrideFields] = useState(initialOverrideFields);
  
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 border-b mb-2">
        <div className="font-semibold text-lg">Conditions</div>
        <div className="flex flex-col gap-2">
          {conditions.map((cond, idx) => (
            <div
              key={idx}
              className="flex flex-row items-end bg-gray-50 rounded border relative gap-2"
            >
              <div className="flex flex-col w-1/2">
                <Select
                  placeholder="Select field"
                  data={allColumns}
                  value={cond.field}
                  onChange={field => {
                    const newConds = [...conditions];
                    newConds[idx].field = field;
                    setConditions(newConds);
                  }}
                  searchable
                  clearable
                />
              </div>
              <div className="flex flex-col w-1/2">
                <TextInput
                  placeholder="Value"
                  value={cond.value}
                  onChange={e => {
                    const newConds = [...conditions];
                    newConds[idx].value = e.target.value;
                    setConditions(newConds);
                  }}
                  disabled={!cond.field}
                />
              </div>
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => {
                  setConditions(conditions.filter((_, i) => i !== idx));
                }}
                title="Remove Condition"
                className="hover:bg-red-100 hover:text-red-700 transition h-9 w-9 flex items-center justify-center"
                style={{ minHeight: 36, minWidth: 36 }}
              >
                <span className="text-lg font-bold">×</span>
              </ActionIcon>
            </div>
          ))}
        </div>
        <Button
          size="small"
          variant="outline"
          color="blue"
          onClick={() => setConditions([...conditions, { field: '', value: '' }])}
          className="w-fit mt-2 self-center"
        >
          + Add Condition
        </Button>
      </div>
      <div className="flex flex-col md:flex-row gap-4 items-end border-b pb-6 mb-2">
        <div className="flex-1 flex flex-col gap-2">
          <div className="font-semibold text-lg">Override Fields</div>
          {overrideFields.map((ovr, idx) => (
            <div
              key={idx}
              className="flex flex-row items-end bg-gray-50 rounded border relative gap-2 mb-2"
            >
              <div className="flex flex-col w-1/2">
                <Select
                  placeholder="Select field to override"
                  data={allColumns}
                  value={ovr.field}
                  onChange={field => {
                    const newOvr = [...overrideFields];
                    newOvr[idx].field = field;
                    setOverrideFields(newOvr);
                  }}
                  searchable
                  clearable
                />
              </div>
              <div className="flex flex-col w-1/2">
                {availableTokenFields.length > 0 ? (
                  <TokenSelector
                    placeholder="Override value"
                    value={ovr.value}
                    onChange={value => {
                      const newOvr = [...overrideFields];
                      newOvr[idx].value = value;
                      setOverrideFields(newOvr);
                    }}
                    availableFields={availableTokenFields}
                    disabled={!ovr.field}
                  />
                ) : (
                  <TextInput
                    placeholder="Override value"
                    value={ovr.value}
                    onChange={e => {
                      const newOvr = [...overrideFields];
                      newOvr[idx].value = e.target.value;
                      setOverrideFields(newOvr);
                    }}
                    disabled={!ovr.field}
                  />
                )}
              </div>
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => {
                  setOverrideFields(overrideFields.filter((_, i) => i !== idx));
                }}
                title="Remove Override Field"
                className="hover:bg-red-100 hover:text-red-700 transition h-9 w-1/2 flex items-center justify-center"
                style={{ minHeight: 36, minWidth: 36 }}
              >
                <span className="text-lg font-bold">×</span>
              </ActionIcon>
            </div>
          ))}
          <Button
            size="small"
            variant="outline"
            color="blue"
            onClick={() => setOverrideFields([...overrideFields, { field: '', value: '' }])}
            className="w-fit mt-2 self-center"
          >
            + Add Override Field
          </Button>
        </div>
      </div>
      <div className="flex flex-row justify-end mb-4 gap-2">
        <Button
          color="green"
          variant="filled"
          disabled={
            conditions.length === 0 ||
            overrideFields.length === 0 ||
            conditions.some(c => !c.field || !c.value) ||
            overrideFields.some(o => !o.field || o.value === undefined || o.value === '')
          }
          onClick={() => onSave(conditions, overrideFields)}
        >
          Save
        </Button>
      </div>
    </div>
  );
});